"use client";

import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch unique tags from contacts
 * Returns sorted list of all unique tags used across contacts
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useContactTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "contacts"],
    queryFn: async () => {
      const response = await fetch("/api/tags/contacts");
      if (!response.ok) throw new Error("Failed to fetch contact tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique tags from tickets
 * Returns sorted list of all unique tags used across tickets
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useTicketTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "tickets"],
    queryFn: async () => {
      const response = await fetch("/api/tags/tickets");
      if (!response.ok) throw new Error("Failed to fetch ticket tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique tags from contact tasks
 * Returns sorted list of all unique tags used across tasks
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useTaskTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "tasks"],
    queryFn: async () => {
      const response = await fetch("/api/tags/tasks");
      if (!response.ok) throw new Error("Failed to fetch task tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique tags from email templates
 * Returns sorted list of all unique tags used across email templates
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useEmailTemplateTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "email-templates"],
    queryFn: async () => {
      const response = await fetch("/api/tags/email-templates");
      if (!response.ok) throw new Error("Failed to fetch email template tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique tags from MSP documents
 * Returns sorted list of all unique tags used across MSP documents
 * Note: Still uses GraphQL as this wasn't in the migration scope
 */
export function useMspDocumentTags() {
  // TODO: Migrate to server-side API if needed
  // For now, keeping GraphQL implementation
  return { tags: [], loading: false, error: null };
}

/**
 * Hook to fetch unique tags from companies
 * Returns sorted list of all unique tags used across companies
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useCompanyTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "companies"],
    queryFn: async () => {
      const response = await fetch("/api/tags/companies");
      if (!response.ok) throw new Error("Failed to fetch company tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique tags from deals
 * Returns sorted list of all unique tags used across deals
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useDealTags() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "deals"],
    queryFn: async () => {
      const response = await fetch("/api/tags/deals");
      if (!response.ok) throw new Error("Failed to fetch deal tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { tags: data || [], loading: isLoading, error };
}

/**
 * Hook to fetch unique technologies from companies
 * Returns sorted list of all unique technologies used across companies
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useCompanyTechnologies() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "company-technologies"],
    queryFn: async () => {
      const response = await fetch("/api/tags/company-technologies");
      if (!response.ok) throw new Error("Failed to fetch company technologies");
      const json = await response.json();
      return json.technologies || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { technologies: data || [], loading: isLoading, error };
}
